
import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Plus, Trash2, GripVertical } from "lucide-react";

interface SubmissionEditorProps {
  submission: any;
  onChange: (updatedSubmission: any) => void;
}

const SubmissionEditor: React.FC<SubmissionEditorProps> = ({ submission, onChange }) => {
  const updateContent = (field: string, value: any) => {
    onChange({
      ...submission,
      content: {
        ...submission.content,
        [field]: value
      }
    });
  };

  const addTag = (newTag: string) => {
    if (newTag.trim() && !submission.content?.tags?.includes(newTag.trim())) {
      const updatedTags = [...(submission.content?.tags || []), newTag.trim()];
      updateContent('tags', updatedTags);
    }
  };

  const removeTag = (tagToRemove: string) => {
    const updatedTags = submission.content?.tags?.filter((tag: string) => tag !== tagToRemove) || [];
    updateContent('tags', updatedTags);
  };

  const addBomItem = () => {
    const newItem = {
      id: Date.now().toString(),
      name: '',
      quantity: 1,
      category: '',
      links: []
    };
    const updatedBom = [...(submission.content?.bom || []), newItem];
    updateContent('bom', updatedBom);
  };

  const updateBomItem = (index: number, field: string, value: any) => {
    const updatedBom = [...(submission.content?.bom || [])];
    updatedBom[index] = { ...updatedBom[index], [field]: value };
    updateContent('bom', updatedBom);
  };

  const removeBomItem = (index: number) => {
    const updatedBom = submission.content?.bom?.filter((_: any, i: number) => i !== index) || [];
    updateContent('bom', updatedBom);
  };

  const addSection = () => {
    const newSection = {
      id: Date.now().toString(),
      title: 'New Section',
      steps: [],
      collapsed: false
    };
    const updatedSections = [...(submission.content?.sections || []), newSection];
    updateContent('sections', updatedSections);
  };

  const updateSection = (sectionIndex: number, field: string, value: any) => {
    const updatedSections = [...(submission.content?.sections || [])];
    updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], [field]: value };
    updateContent('sections', updatedSections);
  };

  const removeSection = (sectionIndex: number) => {
    const updatedSections = submission.content?.sections?.filter((_: any, i: number) => i !== sectionIndex) || [];
    updateContent('sections', updatedSections);
  };

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={submission.content?.name || ''}
                onChange={(e) => updateContent('name', e.target.value)}
                placeholder="Enter submission name"
              />
            </div>
            <div>
              <Label htmlFor="difficulty">Difficulty</Label>
              <Select 
                value={submission.content?.difficulty || 'Beginner'} 
                onValueChange={(value) => updateContent('difficulty', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Beginner">Beginner</SelectItem>
                  <SelectItem value="Intermediate">Intermediate</SelectItem>
                  <SelectItem value="Advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={submission.content?.description || ''}
              onChange={(e) => updateContent('description', e.target.value)}
              placeholder="Enter submission description"
              className="min-h-[100px]"
            />
          </div>

          {/* Tags */}
          <div>
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {submission.content?.tags?.map((tag: string, index: number) => (
                <Badge key={index} variant="secondary" className="flex items-center gap-1">
                  {tag}
                  <button
                    onClick={() => removeTag(tag)}
                    className="ml-1 text-xs hover:text-destructive"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Add a tag"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addTag(e.currentTarget.value);
                    e.currentTarget.value = '';
                  }
                }}
              />
              <Button 
                variant="outline" 
                onClick={() => {
                  const input = document.querySelector('input[placeholder="Add a tag"]') as HTMLInputElement;
                  addTag(input.value);
                  input.value = '';
                }}
              >
                Add
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bill of Materials */}
      {submission.type === 'tutorial' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Bill of Materials</CardTitle>
            <Button variant="outline" size="sm" onClick={addBomItem}>
              <Plus className="h-4 w-4 mr-1" />
              Add Item
            </Button>
          </CardHeader>
          <CardContent>
            {submission.content?.bom?.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No BOM items yet</p>
            ) : (
              <div className="space-y-3">
                {submission.content?.bom?.map((item: any, index: number) => (
                  <div key={index} className="flex gap-2 items-start p-3 border rounded-lg">
                    <GripVertical className="h-4 w-4 text-muted-foreground mt-1" />
                    <div className="flex-1 space-y-2">
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          placeholder="Item name"
                          value={item.name || ''}
                          onChange={(e) => updateBomItem(index, 'name', e.target.value)}
                        />
                        <Input
                          type="number"
                          placeholder="Qty"
                          value={item.quantity || 1}
                          onChange={(e) => updateBomItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        />
                        <Input
                          placeholder="Category"
                          value={item.category || ''}
                          onChange={(e) => updateBomItem(index, 'category', e.target.value)}
                        />
                      </div>
                      
                      {item.links && item.links.length > 0 && (
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Links:</Label>
                          {item.links.map((link: any, linkIndex: number) => (
                            <div key={linkIndex} className="text-xs bg-muted/50 p-2 rounded flex justify-between items-center">
                              <span>{link.name}: {link.url}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBomItem(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tutorial Sections */}
      {submission.type === 'tutorial' && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Tutorial Sections</CardTitle>
            <Button variant="outline" size="sm" onClick={addSection}>
              <Plus className="h-4 w-4 mr-1" />
              Add Section
            </Button>
          </CardHeader>
          <CardContent>
            {submission.content?.sections?.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No sections yet</p>
            ) : (
              <div className="space-y-4">
                {submission.content?.sections?.map((section: any, sectionIndex: number) => (
                  <Card key={sectionIndex} className="border-l-4 border-l-primary">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <Input
                        value={section.title || ''}
                        onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                        className="font-medium"
                        placeholder="Section title"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSection(sectionIndex)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {section.steps?.length || 0} steps in this section
                      </p>
                      {/* Add step management here if needed */}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Part-specific fields */}
      {submission.type === 'part' && (
        <Card>
          <CardHeader>
            <CardTitle>Part Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={submission.content?.category || ''}
                  onChange={(e) => updateContent('category', e.target.value)}
                  placeholder="Part category"
                />
              </div>
              <div>
                <Label htmlFor="subcategory">Subcategory</Label>
                <Input
                  id="subcategory"
                  value={submission.content?.subcategory || ''}
                  onChange={(e) => updateContent('subcategory', e.target.value)}
                  placeholder="Part subcategory"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="manufacturer">Manufacturer</Label>
              <Input
                id="manufacturer"
                value={submission.content?.manufacturer || ''}
                onChange={(e) => updateContent('manufacturer', e.target.value)}
                placeholder="Manufacturer name"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SubmissionEditor;
